import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";

import { Button } from "../core/Button.js";
import { Toggle } from "../core/Toggle.js";
import { cn } from "../foundations/cn.js";
import { BoxArt } from "./BoxArt.js";
import { BoxHeading } from "./BoxHeading.js";
import { useBoxHeroHeading } from "./boxHeroHeading.js";
import { ItemArt } from "./ItemArt.js";
import { QuantitySelector } from "./QuantitySelector.js";

export interface BoxHeroItem {
  id: string;
  image: string;
}

export interface BoxHeroProps {
  name: string;
  /** A short line above the name: a game, a series, "Limited". */
  eyebrow?: string;
  description?: string;
  /** The price of one open, in integer cents. */
  price: number;
  /** Beside the price: "per box · 1 card". */
  priceNote?: string;
  /** Under the price: who made it, the drop table's version, a link to battle with it. */
  meta?: ReactNode;
  /** The box's art; it leads the hero. */
  art: string;
  /** What's inside, drifting behind the art. Fewer than three draws no drift. */
  items: readonly BoxHeroItem[];
  /** How many to open; left out when there's no choice. */
  quantity?: {
    label: string;
    options: readonly number[];
    value: number;
    onValueChange: (value: number) => void;
  };
  /** The main action: "Open for $12.50". */
  open: { label: string; onClick: () => void; disabled?: boolean; loading?: boolean };
  /** Skips the reveal's animation. */
  fastOpen?: { label: string; checked: boolean; onCheckedChange: (checked: boolean) => void };
  /** Beside fast open: why this open is a demo, a refusal. Above the actions when it's a refusal. */
  notice?: ReactNode;
  /** Under everything: the fairness line, where to buy more. */
  footnote?: ReactNode;
  /** h1 on the box's own page. */
  as?: "h1" | "h2";
  className?: string;
}

/**
 * Whether a sticky element is held away from its own place (`marker` marks where it would end),
 * checked as the page scrolls, resizes or settles: it takes its backdrop then, and only then.
 */
function useStuck(sticky: RefObject<HTMLElement | null>, marker: RefObject<HTMLElement | null>) {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const el = sticky.current;
      const end = marker.current;
      if (el && end) {
        setStuck(el.getBoundingClientRect().bottom < end.getBoundingClientRect().top - 1);
      }
    };
    const schedule = () => {
      frame ||= requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // The page settles after mount (the opener loads, fonts and art arrive), with no scroll.
    const resized = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    resized?.observe(document.documentElement);
    if (marker.current?.parentElement) resized?.observe(marker.current.parentElement);
    return () => {
      cancelAnimationFrame(frame);
      resized?.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [sticky, marker]);
  return stuck;
}

/** Two copies of a row, so the strip loops without a seam. */
function DriftRow({ items, reverse }: { items: readonly BoxHeroItem[]; reverse?: boolean }) {
  return (
    <div
      className="flex w-max gap-4 motion-safe:animate-marquee"
      data-reverse={reverse || undefined}
    >
      {[...items, ...items].map((item, i) => (
        <ItemArt
          // biome-ignore lint/suspicious/noArrayIndexKey: the row is two copies of one list
          key={`${item.id}-${i}`}
          src={item.image}
          alt=""
          className="w-16 shrink-0 md:w-20"
        />
      ))}
    </div>
  );
}

/**
 * A box's hero, before it's opened: the art large on its stage with what's
 * inside drifting behind it, then the name and price (unless the page's layout keeps its own
 * heading, `BoxHeroHeadingContext`) and the open. The drift runs a few passes and
 * rests (the ambient-motion budget), and holds still under reduced motion.
 */
export function BoxHero({
  name,
  eyebrow,
  description,
  price,
  priceNote,
  meta,
  art,
  items,
  quantity,
  open,
  fastOpen,
  notice,
  footnote,
  as = "h1",
  className,
}: BoxHeroProps) {
  const withHeading = useBoxHeroHeading();
  const actions = useRef<HTMLDivElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const stuck = useStuck(actions, end);
  const drift = items.length >= 3;
  const half = Math.ceil(items.length / 2);
  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {/* As tall as the viewport leaves room for, so the name and the open fit on arrival, and
          never taller than 4:5 of its width (the opener's root is a container). */}
      <div className="relative isolate flex h-[clamp(9rem,calc(100svh-31rem),21rem)] max-h-[80cqi] items-center justify-center overflow-hidden py-4">
        {drift && (
          <div
            aria-hidden
            className="absolute inset-0 -z-10 flex flex-col justify-center gap-6 opacity-40 [mask-image:linear-gradient(to_right,transparent,black_20%,black_80%,transparent)] [&_[data-reverse]]:[animation-direction:reverse]"
          >
            <DriftRow items={items.slice(0, half)} />
            <DriftRow items={items.slice(half).length > 0 ? items.slice(half) : items} reverse />
          </div>
        )}
        <BoxArt src={art} alt="" floating priority className="w-[42%] max-w-52" />
      </div>
      {withHeading && (
        <BoxHeading
          as={as}
          name={name}
          eyebrow={eyebrow}
          description={description}
          price={price}
          priceNote={priceNote}
          meta={meta}
        />
      )}
      {/* The quantity and the open: held at the viewport's bottom (above a phone's bottom bar)
          while their own place is below it, so the open is always in view on arrival. A direct
          child of the hero, so it can rise over the art. */}
      <div
        ref={actions}
        data-stuck={stuck || undefined}
        className="sticky bottom-[calc(var(--bottom-bar-inset,0px)+0.75rem)] z-10 -m-2 flex flex-col gap-3 rounded-2xl p-2 data-stuck:glass data-stuck:shadow-float"
      >
        {quantity && (
          <QuantitySelector
            label={quantity.label}
            options={quantity.options}
            value={quantity.value}
            onValueChange={quantity.onValueChange}
          />
        )}
        <Button
          size="lg"
          fullWidth
          onClick={open.onClick}
          disabled={open.disabled}
          loading={open.loading}
        >
          {open.label}
        </Button>
      </div>
      {/* Where the open ends when nothing holds it: the hero's gap is taken back around it. */}
      <div ref={end} aria-hidden className="-mt-6 h-px" />
      {(notice || fastOpen) && (
        <div className="-mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">{notice}</div>
          {fastOpen && (
            <span className="flex items-center gap-2 font-body text-body-b2 text-text-secondary">
              <Toggle
                label={fastOpen.label}
                checked={fastOpen.checked}
                onCheckedChange={fastOpen.onCheckedChange}
              />
              <span aria-hidden>{fastOpen.label}</span>
            </span>
          )}
        </div>
      )}
      {footnote && <div className="font-body text-body-b3 text-text-secondary">{footnote}</div>}
    </div>
  );
}
